import React from 'react';

// ─── BUTTON ───────────────────────────────────────────────────────────────────
type ButtonVariant = 'primary' | 'success' | 'danger' | 'warning' | 'ghost' | 'outline' | 'indigo';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

const buttonVariantClasses: Record<ButtonVariant, string> = {
  primary:  'bg-indigo-600 hover:bg-indigo-500 text-white border-transparent',
  indigo:   'bg-indigo-600 hover:bg-indigo-500 text-white border-transparent',
  success:  'bg-green-600  hover:bg-green-500  text-white border-transparent',
  danger:   'bg-red-600    hover:bg-red-500    text-white border-transparent',
  warning:  'bg-amber-600  hover:bg-amber-500  text-white border-transparent',
  ghost:    'bg-slate-800  hover:bg-slate-700  text-slate-200 border-transparent',
  outline:  'bg-transparent hover:bg-slate-800 text-slate-300 border-slate-700',
};

const buttonSizeClasses: Record<ButtonSize, string> = {
  xs: 'px-2.5 py-1 text-xs rounded',
  sm: 'px-3 py-1.5 text-xs rounded-md',
  md: 'px-4 py-2 text-sm rounded-md',
  lg: 'px-6 py-2.5 text-sm rounded-lg font-bold',
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  children,
  className = '',
  disabled,
  ...props
}) => (
  <button
    {...props}
    disabled={disabled || loading}
    className={`
      inline-flex items-center gap-2 font-semibold border transition-all duration-150
      disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap
      ${buttonVariantClasses[variant]}
      ${buttonSizeClasses[size]}
      ${className}
    `.trim().replace(/\s+/g, ' ')}
  >
    {loading ? (
      <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    ) : icon}
    {children}
  </button>
);

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
type BadgeVariant = 'active' | 'inactive' | 'pending' | 'success' | 'danger' | 'warning' | 'info';

const badgeClasses: Record<BadgeVariant, string> = {
  active:   'bg-green-500/15 text-green-400 border-green-500/20',
  inactive: 'bg-slate-500/15 text-slate-400 border-slate-500/20',
  pending:  'bg-amber-500/15 text-amber-400 border-amber-500/20',
  success:  'bg-green-500/15 text-green-400 border-green-500/20',
  danger:   'bg-red-500/15   text-red-400   border-red-500/20',
  warning:  'bg-amber-500/15 text-amber-400 border-amber-500/20',
  info:     'bg-blue-500/15  text-blue-400  border-blue-500/20',
};

interface BadgeProps {
  variant?: BadgeVariant;
  children: React.ReactNode;
  className?: string;
  dot?: boolean;
}

export const Badge: React.FC<BadgeProps> = ({ variant = 'info', children, className = '', dot }) => (
  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold border ${badgeClasses[variant]} ${className}`}>
    {dot && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
    {children}
  </span>
);

// ─── MODAL ────────────────────────────────────────────────────────────────────
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  width?: string;
  footer?: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({ open, onClose, title, children, width = 'max-w-lg', footer }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`relative w-full ${width} max-h-[90vh] flex flex-col bg-slate-900 border border-slate-700/60 rounded-xl shadow-2xl`}>
        {/* Header */}
        <div className="flex-none flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h2 className="text-base font-bold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Body */}
        <div className="flex-1 px-6 py-5 overflow-y-auto scrollbar-hide">{children}</div>
        {/* Footer */}
        {footer && (
          <div className="flex-none px-6 py-4 border-t border-slate-800 flex items-center justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── FORM INPUT ───────────────────────────────────────────────────────────────
interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const FormInput: React.FC<FormInputProps> = ({ label, error, className = '', id, onClick, ...props }) => {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '_');
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={inputId} className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          {label}
        </label>
      )}
      <input
        id={inputId}
        onClick={(e) => {
          if (props.type === 'date' || props.type === 'datetime-local' || props.type === 'time') {
            try {
              (e.target as any).showPicker();
            } catch (err) {}
          }
          if (onClick) onClick(e);
        }}
        style={{ colorScheme: 'dark' }}
        {...props}
        className={`
          w-full px-3 py-2 bg-[#151929] border rounded-md text-sm text-white placeholder-slate-500
          focus:outline-none focus:border-indigo-500 transition-colors
          ${error ? 'border-red-500' : 'border-slate-700'}
          ${className}
        `.trim().replace(/\s+/g, ' ')}
      />
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
};

// ─── FORM SELECT ──────────────────────────────────────────────────────────────
interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: { value: string | number; label: string }[];
}

export const FormSelect: React.FC<FormSelectProps> = ({ label, error, options, className = '', id, ...props }) => {
  const selectId = id || label?.toLowerCase().replace(/\s+/g, '_');
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={selectId} className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          {label}
        </label>
      )}
      <select
        id={selectId}
        {...props}
        className={`
          w-full px-3 py-2 bg-[#151929] border rounded-md text-sm text-white
          focus:outline-none focus:border-indigo-500 transition-colors appearance-none
          ${error ? 'border-red-500' : 'border-slate-700'}
          ${className}
        `.trim().replace(/\s+/g, ' ')}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
};

// ─── FORM MULTI-SELECT ────────────────────────────────────────────────────────
interface FormMultiSelectProps {
  label?: string;
  options: { value: string | number; label: string; searchText?: string }[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  id?: string;
}

export const FormMultiSelect: React.FC<FormMultiSelectProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  placeholder = "Select...",
  className = "",
  id,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  const selectId = id || label?.toLowerCase().replace(/\s+/g, '_');

  const toggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(val => val !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return options;
    const q = searchQuery.toLowerCase();
    return options.filter(opt => 
      opt.label.toLowerCase().includes(q) || 
      (opt.searchText && opt.searchText.toLowerCase().includes(q)) ||
      String(opt.value).toLowerCase().includes(q)
    );
  }, [options, searchQuery]);

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const opt = options.find(o => String(o.value) === String(selectedValues[0]));
      return opt ? opt.label : placeholder;
    }
    return `${selectedValues.length} Selected`;
  };

  return (
    <div className={`flex flex-col gap-1 relative ${className}`} ref={containerRef}>
      {label && (
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          {label}
        </span>
      )}
      <button
        type="button"
        id={selectId}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 bg-[#151929] border border-slate-700 rounded-md text-sm text-white flex items-center justify-between hover:bg-slate-900 transition-colors focus:outline-none focus:border-indigo-500"
      >
        <span className="truncate text-left pr-2 text-slate-200">{getDisplayText()}</span>
        <svg className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1.5 bg-[#1e2337] border border-slate-700/60 rounded-md shadow-2xl z-50 max-h-60 overflow-y-auto scrollbar-hide py-1 flex flex-col">
          <div className="px-2 py-1.5 border-b border-slate-800 bg-[#1e2337] sticky top-0 z-10">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-2.5 py-1.5 bg-[#151929] border border-slate-700 rounded-md text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
            />
          </div>
          <div className="overflow-y-auto flex-1 max-h-48 py-1 scrollbar-hide">
            {filteredOptions.map((opt) => {
              const isChecked = selectedValues.includes(String(opt.value));
              return (
                <label
                  key={opt.value}
                  className="flex items-center justify-between px-3 py-2 hover:bg-slate-800 cursor-pointer transition-colors text-sm text-slate-300 hover:text-white"
                >
                  <span className="truncate font-medium pr-2">{opt.label}</span>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleOption(String(opt.value))}
                    className="w-4 h-4 text-indigo-600 bg-[#151929] border-slate-700 rounded focus:ring-indigo-500 cursor-pointer shrink-0"
                  />
                </label>
              );
            })}
            {filteredOptions.length === 0 && (
              <div className="px-3 py-2 text-xs text-slate-500 text-center">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── FORM TEXTAREA ────────────────────────────────────────────────────────────
interface FormTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export const FormTextarea: React.FC<FormTextareaProps> = ({ label, error, className = '', id, ...props }) => {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '_');
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={inputId} className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          {label}
        </label>
      )}
      <textarea
        id={inputId}
        {...props}
        className={`
          w-full px-3 py-2 bg-slate-950/50 border rounded-md text-sm text-white placeholder-slate-500
          focus:outline-none focus:border-indigo-500 transition-colors resize-none
          ${error ? 'border-red-500' : 'border-slate-700'}
          ${className}
        `.trim().replace(/\s+/g, ' ')}
      />
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
};

// ─── PAGE HEADER ──────────────────────────────────────────────────────────────
interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, actions }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
    <div>
      <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-3">{actions}</div>}
  </div>
);

// ─── CONFIRM DIALOG ───────────────────────────────────────────────────────────
interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  confirmVariant?: ButtonVariant;
  loading?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open, onClose, onConfirm, title, message,
  confirmLabel = 'Confirm', confirmVariant = 'danger', loading
}) => (
  <Modal open={open} onClose={onClose} title={title} width="max-w-sm"
    footer={
      <>
        <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        <Button variant={confirmVariant} size="sm" loading={loading} onClick={onConfirm}>{confirmLabel}</Button>
      </>
    }
  >
    <p className="text-sm text-slate-300">{message}</p>
  </Modal>
);

// ─── CARD ─────────────────────────────────────────────────────────────────────
interface CardProps {
  children: React.ReactNode;
  className?: string;
  title?: string;
  actions?: React.ReactNode;
}

export const Card: React.FC<CardProps> = ({ children, className = '', title, actions }) => (
  <div className={`bg-slate-900 border border-slate-800 rounded-lg shadow-sm ${className}`}>
    {(title || actions) && (
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
        {title && <h3 className="text-sm font-bold text-white">{title}</h3>}
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    )}
    <div className="p-5">{children}</div>
  </div>
);

// ─── TOGGLE (Checkbox styled as toggle) ───────────────────────────────────────
interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
}

export const Toggle: React.FC<ToggleProps> = ({ checked, onChange, label, disabled }) => (
  <label className={`flex items-center gap-3 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`
        relative w-10 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900
        ${checked ? 'bg-indigo-600' : 'bg-slate-700'}
      `}
    >
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
    {label && <span className="text-sm text-slate-300 font-medium">{label}</span>}
  </label>
);
